import {
  billingCycleSchema,
  planStatusSchema,
  planTypeSchema,
  type BillingCycleSchema,
  type CreatePlanDto,
  type PlanStatusSchema,
  type PlanTypeSchema,
  type QueryPlansDto,
  type UpdatePlanDto,
} from '@control-center/shared';
import { z } from 'zod';

import { apiClient } from './api-client';

// =============================================================================
// Response schemas — qa-hardening 2026-05-28
// =============================================================================

// Base shared entre lite e detail
const planBaseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  type: planTypeSchema,
  monthlyPrice: z.string(),
  annualPrice: z.string().nullable(),
  setupFee: z.string(),
  promoPrice: z.string().nullable(),
  billingCycle: billingCycleSchema,
  discountAnnualPct: z.string().nullable(),
  trialDays: z.number(),
  requiresCardForTrial: z.boolean(),
  blocksRepeatTrial: z.boolean(),
  limits: z.record(z.unknown()).nullable(),
  features: z.record(z.unknown()).nullable(),
  // default([]) segura a janela de deploy em que a API ainda não manda o campo.
  checkoutHighlights: z.array(z.string()).default([]),
  status: planStatusSchema,
  autoEmitNf: z.boolean().default(false),
  serviceDescription: z.string().nullable().default(null),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number(),
});

const planListItemSchema = planBaseSchema.extend({
  _count: z.object({ entitlements: z.number() }),
});

const planEntitlementSchema = z.object({
  id: z.string().uuid(),
  planId: z.string().uuid(),
  saasProductId: z.string().uuid(),
  externalPlanId: z.string().uuid(),
  limitsOverride: z.record(z.unknown()).nullable(),
  displayOrder: z.number(),
  createdAt: z.string(),
  saasProduct: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
  externalPlan: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
});

const planDetailSchema = planBaseSchema.extend({
  entitlements: z.array(planEntitlementSchema),
});

const listResponseSchema = z.object({
  items: z.array(planListItemSchema),
  meta: z.object({
    next_cursor: z.string().nullable(),
    total_estimate: z.number().nullable(),
  }),
});

const singleResponseSchema = z.object({ data: planDetailSchema });

const priceHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  planId: z.string().uuid(),
  oldPrice: z.string(),
  newPrice: z.string(),
  field: z.string(),
  reason: z.string().nullable(),
  changedAt: z.string(),
  changedByUserId: z.string().nullable(),
  changedByUser: z.object({ id: z.string(), name: z.string(), email: z.string() }).nullable(),
});

const entitlementHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  planId: z.string().uuid(),
  action: z.enum(['added', 'removed', 'updated']),
  saasProductId: z.string().uuid(),
  oldData: z.record(z.unknown()).nullable(),
  newData: z.record(z.unknown()).nullable(),
  reason: z.string().nullable(),
  changedAt: z.string(),
  changedByUserId: z.string().nullable(),
  saasProduct: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
  changedByUser: z.object({ id: z.string(), name: z.string(), email: z.string() }).nullable(),
});

const priceHistoryResponseSchema = z.object({
  items: z.array(priceHistoryEntrySchema),
  meta: z.object({
    next_cursor: z.string().nullable(),
    total_estimate: z.number().nullable(),
  }),
});

const entitlementsHistoryResponseSchema = z.object({
  items: z.array(entitlementHistoryEntrySchema),
  meta: z.object({
    next_cursor: z.string().nullable(),
    total_estimate: z.number().nullable(),
  }),
});

/**
 * Lite — vem na listagem com _count.entitlements.
 */
export interface PlanListItem {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  type: PlanTypeSchema;
  monthlyPrice: string; // Decimal serializado
  annualPrice: string | null;
  setupFee: string;
  promoPrice: string | null;
  billingCycle: BillingCycleSchema;
  discountAnnualPct: string | null;
  trialDays: number;
  requiresCardForTrial: boolean;
  blocksRepeatTrial: boolean;
  limits: Record<string, unknown> | null;
  features: Record<string, unknown> | null;
  checkoutHighlights: string[];
  status: PlanStatusSchema;
  autoEmitNf: boolean;
  serviceDescription: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  _count: { entitlements: number };
}

/**
 * Detalhe — inclui entitlements + saas/external names.
 */
export interface PlanDetail extends Omit<PlanListItem, '_count'> {
  entitlements: Array<{
    id: string;
    planId: string;
    saasProductId: string;
    externalPlanId: string;
    limitsOverride: Record<string, unknown> | null;
    displayOrder: number;
    createdAt: string;
    saasProduct: { id: string; name: string; slug: string };
    externalPlan: { id: string; name: string; slug: string };
  }>;
}

interface ListResponse {
  items: PlanListItem[];
  meta: { next_cursor: string | null; total_estimate: number | null };
}

export async function fetchPlans(params: QueryPlansDto = { limit: 50 }): Promise<ListResponse> {
  const { data } = await apiClient.get('/api/plans', { params });
  return listResponseSchema.parse(data);
}

export async function fetchPlan(id: string): Promise<PlanDetail> {
  const { data } = await apiClient.get(`/api/plans/${id}`);
  return singleResponseSchema.parse(data).data;
}

export async function createPlan(payload: CreatePlanDto): Promise<PlanDetail> {
  const { data } = await apiClient.post('/api/plans', payload);
  return singleResponseSchema.parse(data).data;
}

export async function updatePlan(id: string, payload: UpdatePlanDto): Promise<PlanDetail> {
  const { data } = await apiClient.patch(`/api/plans/${id}`, payload);
  return singleResponseSchema.parse(data).data;
}

export async function deletePlan(id: string, version: number): Promise<void> {
  await apiClient.delete(`/api/plans/${id}`, { data: { version } });
}

/**
 * Hard delete — só funciona em plano "descontinuado" sem nenhuma subscription
 * (ativa OU histórica). Cascade apaga entitlements + history. Pra expurgar
 * planos de teste.
 */
export async function hardDeletePlan(id: string, version: number): Promise<void> {
  await apiClient.delete(`/api/plans/${id}?hard=true`, { data: { version } });
}

// =============================================================================
// History endpoints — Story 2.5b
// =============================================================================

export interface PriceHistoryItem {
  id: string;
  planId: string;
  oldPrice: string;
  newPrice: string;
  field: string;
  reason: string | null;
  changedAt: string;
  changedByUserId: string | null;
  changedByUser: { id: string; name: string; email: string } | null;
}

export interface EntitlementHistoryItem {
  id: string;
  planId: string;
  action: 'added' | 'removed' | 'updated';
  saasProductId: string;
  oldData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
  reason: string | null;
  changedAt: string;
  changedByUserId: string | null;
  saasProduct: { id: string; name: string; slug: string };
  changedByUser: { id: string; name: string; email: string } | null;
}

interface HistoryQuery {
  cursor?: string;
  limit?: number;
}

interface PriceHistoryResponse {
  items: PriceHistoryItem[];
  meta: { next_cursor: string | null; total_estimate: number | null };
}

interface EntitlementsHistoryResponse {
  items: EntitlementHistoryItem[];
  meta: { next_cursor: string | null; total_estimate: number | null };
}

export async function fetchPlanPriceHistory(
  planId: string,
  query: HistoryQuery & { field?: string } = {},
): Promise<PriceHistoryResponse> {
  const { data } = await apiClient.get(`/api/plans/${planId}/price-history`, { params: query });
  return priceHistoryResponseSchema.parse(data);
}

export async function fetchPlanEntitlementsHistory(
  planId: string,
  query: HistoryQuery & { action?: 'added' | 'removed' | 'updated' } = {},
): Promise<EntitlementsHistoryResponse> {
  const { data } = await apiClient.get(`/api/plans/${planId}/entitlements-history`, {
    params: query,
  });
  return entitlementsHistoryResponseSchema.parse(data);
}
